/**
 * CMMC scoring engine — V102-6-2 / ADR-0058 §3 (manifest `scoring.engine = 'cmmc'`).
 *
 * Inputs: requirement status from the `objective` readiness engine (reviewed
 * determinations only) and the period's rule set (pinned at Audit, else the
 * current approved one; the result says which). Every value — points,
 * partial cases, threshold, eligibility, the never-POA&M set — comes from the
 * rule set, never from code.
 *
 *   - **Level 1**: `met` iff every L1 requirement's mapped L2 rows are met
 *     (N/A = met, as readiness reports it). No number, no POA&M.
 *   - **Level 2**: score = max − Σ points(unmet), applying a partial case when
 *     one is claimed for an unmet requirement; ssp_present = a reviewed met on
 *     the SSP requirement (3.12.4) or an issued `cmmc_ssp` package for the
 *     period; conditional_eligible = score ≥ threshold ∧ every unmet
 *     requirement POA&M-eligible ∧ no never-eligible requirement unmet ∧ SSP.
 *   - **Unassessed** requirements (readiness `undetermined` / `no_evidence`)
 *     score as unmet (ADR-0058 owner choice, 2026-10-05) and are listed
 *     separately with their points, so "closed by collection alone" stays visible.
 *   - **POA&M state** (ADR-0058 §4): an open item past its closeout date
 *     lapses conditional status (`conditional_lapsed`), even before the sweep
 *     marks it expired.
 *
 * Partial cases (3.5.3 MFA, 3.13.11 FIPS) are judgments a person records:
 * the condition is taken from an open POA&M item that names it (3.13.11's
 * exception), else from the caller's `partialClaims`. With no claim the full
 * points are deducted (the conservative default).
 */
import { getProgramManifest } from '../programs/manifests.js';
import { computeObjectiveReadiness } from './objective-readiness-service.js';
import { parseCmmcRules, resolveRuleSetForPeriod, } from './program-rule-set-service.js';
export const CMMC_SCORE_FORMAT = 'gacpro.cmmc-score/1';
export class CmmcScoringError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'CmmcScoringError';
    }
}
// ---- pure rules helpers (shared with poam-service) --------------------------------------
export function ruleFor(rules, requirementId) {
    return rules.requirements.find((r) => r.id === requirementId);
}
/** Points deducted for an unmet requirement, applying a claimed partial case. */
export function pointsFor(rules, requirementId, partialCondition) {
    const r = ruleFor(rules, requirementId);
    if (!r)
        throw new CmmcScoringError('invalid', `Requirement ${requirementId} is not in the rule set.`);
    if (!partialCondition)
        return r.points ?? 0;
    const c = r.partial?.find((p) => p.condition === partialCondition);
    if (!c)
        throw new CmmcScoringError('invalid', `${requirementId} has no partial case '${partialCondition}'.`);
    return c.points;
}
/** POA&M eligibility of an unmet requirement under the rule set (32 CFR 170.21 as content). */
export function poamEligibility(rules, requirementId, partialCondition) {
    const r = ruleFor(rules, requirementId);
    if (!r)
        throw new CmmcScoringError('invalid', `Requirement ${requirementId} is not in the rule set.`);
    if (r.never_poam || rules.poam.never_eligible.includes(r.id))
        return 'never';
    if (r.poam_eligible === 'yes')
        return 'eligible';
    if (r.poam_eligible === 'exception') {
        const ex = rules.poam.exceptions.find((e) => e.requirement === r.id);
        return ex && partialCondition === ex.when_condition ? 'exception' : 'ineligible';
    }
    return 'ineligible';
}
export function scoreCmmc(input) {
    const { rules } = input;
    const now = (input.now ?? new Date()).toISOString();
    const statusOf = (id) => input.statuses.get(id) ?? 'undetermined';
    // Level 1: every mapped L2 row met.
    const l1Reqs = rules.levels.L1.requirements.map((q) => {
        const failing = q.l2.filter((id) => statusOf(id) !== 'met');
        return { requirement_id: q.id, status: (failing.length ? 'not_met' : 'met'), l2: [...q.l2], failing };
    });
    const l1Met = l1Reqs.filter((r) => r.status === 'met').length;
    const level1 = {
        status: l1Met === l1Reqs.length ? 'met' : 'not_met',
        outcome_id: rules.outcomes.L1,
        requirements: l1Reqs,
        counts: { requirements: l1Reqs.length, met: l1Met, not_met: l1Reqs.length - l1Met },
        not_yet_assessed: [...new Set(l1Reqs.flatMap((r) => r.failing))].filter((id) => statusOf(id) !== 'not_met').sort(),
    };
    let level2 = null;
    if (input.level === '2') {
        const items = input.poamItems ?? [];
        const openItem = new Map(items.filter((i) => i.status === 'open').map((i) => [i.requirement_id, i]));
        // Partial claims: an open POA&M item's recorded condition, else the caller's claim.
        const claims = { ...(input.partialClaims ?? {}) };
        for (const i of openItem.values())
            if (i.partial_condition && !claims[i.requirement_id])
                claims[i.requirement_id] = i.partial_condition;
        const unmet = [];
        let deducted = 0;
        let met = 0;
        let na = 0;
        for (const r of rules.requirements) {
            const s = statusOf(r.id);
            if (s === 'met') {
                met++;
                if (input.allNotApplicable?.has(r.id))
                    na++;
                continue;
            }
            const assessed = s === 'not_met';
            // A partial case is a judgment about how the requirement is unmet: it
            // applies only to an assessed (not_met) requirement.
            const condition = assessed ? claims[r.id] ?? null : null;
            const points = pointsFor(rules, r.id, condition);
            deducted += points;
            unmet.push({
                requirement_id: r.id, status: s, assessed, points, partial_condition: condition,
                poam_eligibility: poamEligibility(rules, r.id, condition), poam_id: openItem.get(r.id)?.poam_id ?? null,
            });
        }
        const score = rules.max_score - deducted;
        const sspReq = rules.ssp_requirement.requirement;
        const sspByReq = statusOf(sspReq) === 'met';
        const sspPresent = sspByReq || !!input.sspPackageId;
        const blockers = [];
        if (score < rules.conditional_threshold)
            blockers.push('below_threshold');
        if (unmet.some((u) => u.poam_eligibility === 'ineligible'))
            blockers.push('ineligible_unmet');
        if (unmet.some((u) => u.poam_eligibility === 'never'))
            blockers.push('never_eligible_unmet');
        if (!sspPresent)
            blockers.push('no_ssp');
        const conditionalEligible = blockers.length === 0;
        const finalEligible = unmet.length === 0 && sspPresent && score === rules.max_score;
        const expired = items.filter((i) => i.status === 'expired' || (i.status === 'open' && i.closeout_due_at !== null && i.closeout_due_at <= now)).map((i) => i.poam_id);
        const status = finalEligible ? 'final'
            : conditionalEligible ? (expired.length ? 'conditional_lapsed' : 'conditional')
                : 'not_met';
        const notAssessed = unmet.filter((u) => !u.assessed);
        const mustSet = rules.requirements.filter((r) => { const e = poamEligibility(rules, r.id, null); return e === 'never' || e === 'ineligible'; });
        level2 = {
            score,
            max_score: rules.max_score,
            minimum: rules.minimum.value,
            conditional_threshold: rules.conditional_threshold,
            ssp_present: sspPresent,
            ssp_basis: sspByReq ? 'requirement_met' : input.sspPackageId ? 'package' : null,
            ssp_package_id: input.sspPackageId ?? null,
            conditional_eligible: conditionalEligible,
            final_eligible: finalEligible,
            status,
            blockers,
            outcome_id: rules.outcomes.L2,
            unmet,
            not_yet_assessed: notAssessed.map((u) => u.requirement_id),
            points_not_yet_assessed: notAssessed.reduce((n, u) => n + u.points, 0),
            closed_by_collection_alone: [...(input.closedByCollectionAlone ?? [])].filter((id) => ruleFor(rules, id)).sort(),
            counts: {
                requirements: rules.requirements.length, met, not_met: unmet.length - notAssessed.length,
                not_yet_assessed: notAssessed.length, not_applicable: na,
            },
            poam: {
                open: openItem.size,
                expired,
                closed: items.filter((i) => i.status === 'closed').length,
                // Only a clocked item (after conditional status) has a closeout to affirm.
                closeout_affirmation_pending: items.filter((i) => i.status === 'closed' && i.closeout_due_at !== null && !i.closeout_affirmed).map((i) => i.poam_id),
                unmet_without_item: unmet.filter((u) => !openItem.has(u.requirement_id)).map((u) => u.requirement_id),
            },
            must_met: { total: mustSet.length, met: mustSet.filter((r) => statusOf(r.id) === 'met').length },
            ssp_requirement_id: sspReq,
        };
    }
    const outcomes = [{ outcome_id: rules.outcomes.L1, level: '1', status: level1.status }];
    if (level2)
        outcomes.push({ outcome_id: rules.outcomes.L2, level: '2', status: level2.status });
    return {
        format: CMMC_SCORE_FORMAT, program: 'cmmc', period_id: input.periodId, level: input.level,
        rule_set: input.ruleSet, level1, level2, outcomes,
    };
}
/** Status per requirement from objective readiness (reviewed determinations only). */
export function statusesFromReadiness(r) {
    const statuses = new Map();
    const allNa = new Set();
    for (const q of r.requirements) {
        statuses.set(q.requirement_id, q.status);
        if (q.all_not_applicable)
            allNa.add(q.requirement_id);
    }
    return { statuses, allNa };
}
/** The CMMC score of a period (workspace-scoped; 404-style error on another tenant's period). */
export async function computeCmmcScore(db, input) {
    const content = input.contentDb ?? db;
    const pr = await db.query(`SELECT p.period_id, p.profile_id, p.period_start, p.period_end, c.program, c.program_options_json
       FROM audit_periods p JOIN certification_profiles c ON c.profile_id = p.profile_id AND c.workspace_id = p.workspace_id
      WHERE p.period_id = ? AND p.workspace_id = ? LIMIT 1`, [input.periodId, input.workspaceId]);
    const period = pr.results[0];
    if (!period)
        throw new CmmcScoringError('not_found', 'Audit period not found.');
    if (getProgramManifest(period.program)?.scoring.engine !== 'cmmc')
        throw new CmmcScoringError('invalid', 'This program is not scored by the CMMC engine.');
    let level = '2';
    try {
        const o = JSON.parse(period.program_options_json || '{}');
        if (o.level === '1')
            level = '1';
    }
    catch { /* the manifest validated the options on write */ }
    const resolved = await resolveRuleSetForPeriod(db, { workspaceId: input.workspaceId, periodId: input.periodId, programKey: period.program, contentDb: content });
    if (!resolved)
        throw new CmmcScoringError('rules_not_approved', 'No approved CMMC rule set yet: approve the CMMC program rules in the review queue.');
    const rules = parseCmmcRules(resolved.ruleSet);
    const readiness = await computeObjectiveReadiness(db, { workspaceId: input.workspaceId, periodId: input.periodId, contentDb: content });
    const { statuses, allNa } = statusesFromReadiness(readiness);
    const ssp = await db.query(`SELECT package_id FROM certification_packages
      WHERE workspace_id = ? AND profile_id = ? AND kind = 'cmmc_ssp' AND period_start = ? AND period_end = ?
      ORDER BY created_at DESC, package_id DESC LIMIT 1`, [input.workspaceId, period.profile_id, period.period_start, period.period_end]);
    const items = await db.query(`SELECT i.poam_id, i.requirement_id, i.status, i.closeout_due_at, i.partial_condition, i.closeout_affirmation_id,
            CASE WHEN a.state = 'affirmed' THEN 1 ELSE 0 END AS affirmed
       FROM poam_items i
       LEFT JOIN affirmation_records a ON a.affirmation_id = i.closeout_affirmation_id AND a.workspace_id = i.workspace_id
      WHERE i.workspace_id = ? AND i.period_id = ?
      ORDER BY i.opened_at, i.poam_id`, [input.workspaceId, input.periodId]);
    return scoreCmmc({
        rules,
        ruleSet: { rule_set_id: resolved.ruleSet.rule_set_id, version: resolved.ruleSet.version, content_sha256: resolved.ruleSet.content_sha256, basis: resolved.basis },
        periodId: input.periodId,
        level,
        statuses,
        allNotApplicable: allNa,
        closedByCollectionAlone: readiness.closed_by_collection_alone,
        sspPackageId: ssp.results[0]?.package_id ?? null,
        partialClaims: input.partialClaims,
        poamItems: items.results.map(({ affirmed, ...i }) => ({ ...i, closeout_affirmed: affirmed === 1 })),
        now: input.now,
    });
}
//# sourceMappingURL=cmmc-scoring-service.js.map